import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useRef, useState, useEffect } from "react";

interface DetailFieldMappingProps {
  title: string;
  selectedItems: string[];
  details: Record<string, string>;
  onDetailsChange: (details: Record<string, string>) => void;
  placeholder?: string;
  maxLength?: number;
}

export function DetailFieldMapping({
  title,
  selectedItems,
  details,
  onDetailsChange,
  placeholder = "Add more details...",
  maxLength = 500,
}: DetailFieldMappingProps) {
  // Local state to allow typing
  const [localDetails, setLocalDetails] = useState<Record<string, string>>({});
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Initialize local state from props
  useEffect(() => {
    setLocalDetails(details);
  }, [details]);

  const handleDetailChange = (item: string, value: string) => {
    // Update local state immediately for responsive UI
    const newDetails = {
      ...localDetails,
      [item]: value,
    };
    setLocalDetails(newDetails);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced update
    timeoutRef.current = setTimeout(() => {
      // Build the complete details object with only selected items
      const updatedDetails: Record<string, string> = {};

      selectedItems.forEach((selectedItem) => {
        const currentValue = newDetails[selectedItem] || "";
        // Only include non-empty values
        if (currentValue) {
          updatedDetails[selectedItem] = currentValue;
        }
      });

      onDetailsChange(updatedDetails);
    }, 1000);
  };

  if (selectedItems.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-neutral-600">{title}</Label>
      <div className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        {selectedItems.map((item) => {
          const value = localDetails[item] || "";
          return (
            <div key={item} className="space-y-2">
              <Label className="text-sm font-medium text-neutral-700">
                {item}
              </Label>
              <Textarea
                value={value}
                onChange={(e) => handleDetailChange(item, e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                className="min-h-[80px] resize-none bg-white"
              />
              <p className="text-xs text-neutral-500">
                {value.length}/{maxLength} characters
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
